import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiUrl } from '../config/api'
import { supabase } from '../config/supabase'

export interface ActiveSubscriptionState {
  checkingSub: boolean
  hasActiveSub: boolean
}

/**
 * Detect whether the current user has an active or trialing subscription.
 * Mirrors the strategies used in `ProtectedRoute` so behavior is consistent.
 */
export const useActiveSubscription = (): ActiveSubscriptionState => {
  const { user } = useAuth()
  const [checkingSub, setCheckingSub] = useState<boolean>(!!user)
  const [hasActiveSub, setHasActiveSub] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      if (!user) {
        setHasActiveSub(false)
        setCheckingSub(false)
        return
      }

      setCheckingSub(true)
      try {
        // Strategy 1: Recent subscriptionId from onboarding (fast-path)
        const subId = typeof window !== 'undefined' ? localStorage.getItem('subscriptionId') : null
        if (subId) {
          const start = Date.now()
          while (!cancelled && Date.now() - start < 12000) {
            try {
              const r = await fetch(apiUrl(`/api/subscription/${subId}`))
              if (r.ok) {
                const s = await r.json()
                const st = (s?.status || '').toLowerCase()
                if (st === 'active' || st === 'trialing') {
                  setHasActiveSub(true)
                  try { localStorage.removeItem('subscriptionId') } catch (_) {}
                  setCheckingSub(false)
                  return
                }
              }
            } catch (_) {}
            await new Promise(res => setTimeout(res, 800))
          }
        }

        // Strategy 2: Unified status endpoint by email
        try {
          const resp = user.email
            ? await fetch(apiUrl(`/api/subscription-status?email=${encodeURIComponent(user.email)}`))
            : null
          if (resp && resp.ok) {
            const data = await resp.json()
            const status = (data?.status || '').toLowerCase()
            if (status === 'active' || status === 'trialing') {
              setHasActiveSub(true)
              setCheckingSub(false)
              return
            }
          }
        } catch (_) {}

        // Strategy 3: Supabase user metadata (updated by webhook)
        try {
          const { data } = await supabase.auth.getUser()
          const meta = data?.user?.user_metadata as any
          const st = (meta?.subscription_status || '').toLowerCase()
          if (st === 'active' || st === 'trialing') {
            setHasActiveSub(true)
            setCheckingSub(false)
            return
          }
        } catch (_) {}

        setHasActiveSub(false)
      } catch (_) {
        setHasActiveSub(false)
      } finally {
        if (!cancelled) setCheckingSub(false)
      }
    }

    check()
    return () => { cancelled = true }
  }, [user])

  return { checkingSub, hasActiveSub }
}


